import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import SystemPlanService from '../../services/system/systemPlanService';
import {
  loadingSystemPlans,
  updateActivePlan,
  setEditingFeatures,
  toggleFeature,
  setFeatureEnabled,
  clearEditingFeatures,
  startLoadingFeatures,
  stopLoadingFeatures,
} from '../../store/slices/system/systemPlanSlice';
import useAlerts from '../useAlerts';

export const useSystemPlans = () => {
  const dispatch: AppDispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const [updatingFeatureCodes, setUpdatingFeatureCodes] = useState<string[]>(
    []
  );

  const { plans, editingFeatures, editingPlanCode, isLoadingFeatures } =
    useSelector((state: RootState) => state.systemPlans);

  /**
   * 📦 Obtener planes
   */
  const fetchPlans = async () => {
    try {
      const response = await SystemPlanService.fetchPlans();
      dispatch(loadingSystemPlans(response.data.plans));
    } catch (error: any) {
      console.error('Error cargando planes:', error);
      showAlert(
        'error',
        'Error',
        error?.response?.data?.message || 'No se pudieron cargar los planes'
      );
    }
  };

  /**
   * 🔄 Cambiar plan activo
   */
  const changePlan = async (planCode: string) => {
    try {
      const response = await SystemPlanService.changeActivePlan(planCode);
      const { type, title, message, data, statusCode } = response;

      if (statusCode === 200) {
        dispatch(updateActivePlan(data));

        // 🔥 limpiar edición al cambiar plan
        dispatch(clearEditingFeatures());

        showAlert(type, title, message);
      }
    } catch (error: any) {
      console.error('Error cambiando plan:', error);
      showAlert(
        'error',
        'Error al cambiar plan',
        error?.response?.data?.message || 'No se pudo cambiar el plan'
      );
    }
  };

  /**
   * 🔥 Cargar features (y guardarlos en redux)
   */
  const loadPlanFeatures = async (planCode: string) => {
    try {
      dispatch(startLoadingFeatures());

      const response = await SystemPlanService.getPlanFeatures(planCode);

      dispatch(
        setEditingFeatures({
          planCode,
          features: response.data,
        })
      );
    } catch (error) {
      showAlert('error', 'Error', 'No se pudieron cargar los features');
    } finally {
      dispatch(stopLoadingFeatures());
    }
  };

  /**
   * 🔁 Toggle feature
   */
  const onToggleFeature = async (planCode: string, code: string) => {
    const feature = editingFeatures.find((item) => item.code === code);
    if (!feature || updatingFeatureCodes.includes(code)) return;

    const previousEnabled = feature.enabled;
    dispatch(toggleFeature(code));
    setUpdatingFeatureCodes((current) => [...current, code]);

    try {
      const response = await SystemPlanService.updatePlanFeature(
        planCode,
        code,
        { enabled: !previousEnabled }
      );
      dispatch(setFeatureEnabled({ code, enabled: response.data.enabled }));
    } catch (error: any) {
      dispatch(setFeatureEnabled({ code, enabled: previousEnabled }));
      showAlert(
        'error',
        'Error al actualizar funcionalidad',
        error?.response?.data?.message ||
          'No se pudo actualizar la funcionalidad'
      );
    } finally {
      setUpdatingFeatureCodes((current) =>
        current.filter((featureCode) => featureCode !== code)
      );
    }
  };

  /**
   * 🧹 Limpiar (cuando cambias de plan manualmente)
   */
  const clearEditing = () => {
    dispatch(clearEditingFeatures());
  };

  return {
    plans,
    editingFeatures,
    editingPlanCode,
    isLoadingFeatures,
    updatingFeatureCodes,

    fetchPlans,
    changePlan,

    loadPlanFeatures,
    onToggleFeature,
    clearEditing,
  };
};
